import * as THREE from 'three';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { pixelRatio2d } from '@/editor/math/const';
import {
  ENTITY_RENDER_ORDER,
  ENTITY_PRIORITY,
  filledCircleColor,
  selectedOutlineColor,
  DefaultEntitySize
} from '@/editor/constant/entity';
import BaseEntity from './BaseEntity';
import { disposeAllChildrenThenRemoveFromParent } from './utils';
import { EContainerType, EEntityStatus } from '../types/entity';
import { EEntityType, EExtremePointType } from '../types';
import { getCircleContour } from '../utils/face';
import { Vector3 } from 'three';
import { Line2, LineGeometry } from 'three/examples/jsm/Addons.js';
import { globalStores } from '../stores';
import { MaterialStore } from '../stores/materialStore';

export type TWallExtremePointHostType = EEntityType.Wall | EEntityType.SeparateWall;

export class WallExtremePointEntity extends BaseEntity<EEntityType.WallExtremePoint> {
  private _pointGroup: THREE.Group = new THREE.Group();
  private _hollowCircle: Line2 | null = null;
  private _filledCircle: THREE.Mesh | null = null;
  private _position: IPoint2d = { x: 0, y: 0 };
  private _wallId: string;
  private _wallType: TWallExtremePointHostType;
  private _pointType: EExtremePointType;
  private modelCreated: boolean = false;
  private _scale: number = 1;

  status: EEntityStatus = EEntityStatus.normal;

  constructor(wallId: string, pointType: EExtremePointType, wallType: TWallExtremePointHostType = EEntityType.Wall) {
    super({ type: EEntityType.WallExtremePoint, id: getExtremePointId(wallId, pointType, wallType) });
    this._wallId = wallId;
    this._wallType = wallType;
    this._pointType = pointType;

    this._pointGroup.userData.type = EEntityType.WallExtremePoint;
    this._pointGroup.userData.entityId = this._id;
    this._pointGroup.userData.priority = ENTITY_PRIORITY.wallExtremePoint;
    this._pointGroup.userData.containerType = EContainerType.entity;

    globalStores.editorStateStore.sceneInfo.scene2d.intersectableGroup.add(this._pointGroup);

    this.setDisplayOrder(ENTITY_RENDER_ORDER.wallExtremePoint);
  }

  getData() {
    return {
      wallId: this._wallId,
      wallType: this._wallType,
      pointType: this._pointType,
      position: this._position
    };
  }

  get position() {
    return this._position;
  }

  override set selected(value: boolean) {
    if (!this._hollowCircle || !this._filledCircle) return;

    this._selected = value;
    if (this._selected) {
      this._hollowCircle.visible = false;
    } else {
      this._hollowCircle.visible = true;
    }
    const color = value ? selectedOutlineColor : filledCircleColor;
    this._filledCircle.material = new THREE.MeshBasicMaterial({ color });
  }

  override get selected() {
    return this._selected;
  }

  private createModel() {
    this._selected = false;

    const radius = DefaultEntitySize.WallExtremePointRadius;
    const circlePoints = getCircleContour({ x: 0, y: 0 }, radius, 32);
    circlePoints.push(circlePoints[0]);
    const circleGeometry = new LineGeometry().setFromPoints(
      circlePoints.map((point) => new Vector3(point.x / pixelRatio2d, point.y / pixelRatio2d, 0.001))
    );
    this._hollowCircle = new Line2(circleGeometry, MaterialStore.sharedMaterials.extremePointOutline);

    const filledGeometry = new THREE.CircleGeometry(radius / pixelRatio2d, 32);
    this._filledCircle = new THREE.Mesh(filledGeometry, MaterialStore.sharedMaterials.extremePointFilled);

    this._pointGroup.add(this._filledCircle, this._hollowCircle);

    this.modelCreated = true;
  }

  override update(params: { position: IPoint2d; scale: number }) {
    if (!this.modelCreated) this.createModel();

    this._position = params.position;
    this._scale = params.scale;

    this._pointGroup.position.setX(this._position.x / pixelRatio2d);
    this._pointGroup.position.setY(this._position.y / pixelRatio2d);
    this._pointGroup.scale.set(this._scale, this._scale, 1);
  }

  setDisplayOrder(order: number) {
    this._pointGroup.position.z = order;
  }

  // 动态调整圆形的 scale 以抵消 zoom
  updateScale(scale: number) {
    this._pointGroup.scale.set(scale, scale, 1);
  }

  override dispose() {
    disposeAllChildrenThenRemoveFromParent(this._pointGroup);
  }
}

export function getExtremePointId(wallId: string, pointType: EExtremePointType, wallType: TWallExtremePointHostType) {
  return `${wallType}_${wallId}_${pointType}`;
}
